/**
 * Donations API Service
 * Handles donation-related API calls
 */

import { ApiClient } from '../core/api-client';
import { isApiError } from '../core/errors';
import { logger } from '../../utils/logger';

export interface Donation {
  id: string;
  amount: number;
  currency?: string;
  date: string;
  category?: string;
  status?: 'pending' | 'completed' | 'failed';
  description?: string;
  donorName?: string;
  paymentMethod?: string;
}

export interface DonationsResponse {
  data: Donation[];
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface DonationResponse {
  data: Donation;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export class DonationService {
  private client: ApiClient;

  constructor() {
    this.client = ApiClient.getInstance(API_BASE_URL);
  }

  /**
   * Get donations with filtering
   */
  async getDonations(options?: {
    limit?: number;
    offset?: number;
    status?: string;
    category?: string;
    congregationId?: string;
  }): Promise<{ donations: Donation[]; total: number }> {
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.offset) params.append('offset', options.offset.toString());
      if (options?.status) params.append('status', options.status);
      if (options?.category) params.append('category', options.category);
      if (options?.congregationId) params.append('congregationId', options.congregationId);

      const url = `/devotee/donation${params.toString() ? '?' + params.toString() : ''}`;
      const response = await this.client.get<DonationsResponse>(url, {
        credentials: 'include',
      });

      // Handle both direct array response and wrapped response
      const donationsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data || []);

      return {
        donations: donationsData,
        total: Array.isArray(response.data) ? response.data.length : (response.data?.pagination?.total || 0),
      };
    } catch (error) {
      if (isApiError(error) && (error.statusCode === 401 || error.statusCode === 403)) {
        // Gracefully handle auth errors
        return { donations: [], total: 0 };
      }
      console.debug('[DonationService] Failed to fetch donations', error);
      return { donations: [], total: 0 };
    }
  }

  /**
   * Get donation by ID
   */
  async getDonationById(donationId: string): Promise<Donation | null> {
    try {
      const response = await this.client.get<DonationResponse>(`/devotee/donation/${donationId}`, {
        credentials: 'include',
      });

      return response.data?.data || null;
    } catch (error) {
      if (isApiError(error) && error.statusCode === 404) {
        logger.warn(`Donation ${donationId} not found`);
        return null;
      }

      logger.error('Failed to fetch donation', error);
      return null;
    }
  }

  /**
   * Get user's donation history
   */
  async getDonationHistory(limit?: number, offset?: number): Promise<Donation[]> {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());

      const url = `/donation/history${params.toString() ? '?' + params.toString() : ''}`;
      const response = await this.client.get<DonationsResponse>(url, {
        credentials: 'include',
      });

      return response.data?.data || [];
    } catch (error) {
      logger.error('Failed to fetch donation history', error);
      return [];
    }
  }

  /**
   * Create donation
   */
  async createDonation(data: Partial<Donation>): Promise<Donation> {
    try {
      const response = await this.client.post<DonationResponse>('/devotee/donation', data, {
        credentials: 'include',
      });

      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }

      return response.data.data;
    } catch (error) {
      logger.error('Failed to create donation', error);
      throw error;
    }
  }

  /**
   * Initiate donation payment
   */
  async initiateDonationPayment(data: {
    amount: number;
    currency?: string;
    category?: string;
  }): Promise<{ paymentUrl: string; sessionId: string }> {
    try {
      const response = await this.client.post<{
        data: { paymentUrl: string; sessionId: string };
      }>('/donation/payment/initiate', data, {
        credentials: 'include',
      });

      return response.data?.data || { paymentUrl: '', sessionId: '' };
    } catch (error) {
      logger.error('Failed to initiate payment', error);
      throw error;
    }
  }

  /**
   * Verify donation payment
   */
  async verifyDonationPayment(sessionId: string): Promise<Donation | null> {
    try {
      const response = await this.client.post<DonationResponse>(
        `/donation/payment/verify`,
        { sessionId },
        { credentials: 'include' }
      );

      return response.data?.data || null;
    } catch (error) {
      logger.error('Failed to verify payment', error);
      return null;
    }
  }

  /**
   * Cancel donation
   */
  async cancelDonation(donationId: string): Promise<void> {
    try {
      await this.client.post(`/donation/${donationId}/cancel`, {}, { credentials: 'include' });
    } catch (error) {
      logger.error('Failed to cancel donation', error);
      throw error;
    }
  }
}

// Export singleton instance
export const donationService = new DonationService();
